import { 
  doc, 
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  Timestamp,
  deleteField // Add this import to fix the undefined error
} from 'firebase/firestore';
import { db } from '../firebase';

const expensesCache = {
  data: null,
  timestamp: null,
  expiryTime: 5 * 60 * 1000 // 5 minutes cache validity
};

// Get expenses with caching
const getExpensesWithCache = async (userId) => {
  const now = Date.now();
  
  // If we have valid cached data, return it
  if (
    expensesCache.data && 
    expensesCache.timestamp && 
    (now - expensesCache.timestamp) < expensesCache.expiryTime
  ) {
    console.log('Using cached expenses data');
    return expensesCache.data;
  }
  
  // Otherwise fetch fresh data
  console.log('Fetching fresh expenses data');
  const expenses = await expenseService.fetchExpenses(userId);
  
  // Update cache
  expensesCache.data = expenses;
  expensesCache.timestamp = now;
  
  return expenses;
};

// Invalidate cache (call this after adding, updating or deleting expenses)
const invalidateExpensesCache = () => {
  expensesCache.data = null;
  expensesCache.timestamp = null;
};

// Add to your other CRUD operations:
const addExpenseWithCacheInvalidation = async (userId, expenseData) => {
  const result = await expenseService.addExpense(userId, expenseData);
  invalidateExpensesCache();
  return result;
};

const updateExpenseWithCacheInvalidation = async (expenseId, expenseData) => {
  const result = await expenseService.updateExpense(expenseId, expenseData);
  invalidateExpensesCache();
  return result;
};

export const expenseService = {
  // Fetch all expenses for a user
  async fetchExpenses(userId, cutoffDate = null) {
    try {
      // Check for internet connectivity
      const isOnline = navigator.onLine;
      
      // If offline, try to use cached data
      if (!isOnline) {
        console.log('Offline mode detected, using cached expenses');
        const cachedData = localStorage.getItem('cachedExpenses');
        if (cachedData) {
          return JSON.parse(cachedData);
        }
        return [];
      }
      
      const expensesRef = collection(db, 'expenses');
      
      // Build query with or without date filter
      let q;
      if (cutoffDate) {
        console.log('Fetching expenses with cutoff date:', cutoffDate);
        const cutoffTimestamp = new Date(cutoffDate);
        
        q = query(
          expensesRef, 
          where('userId', '==', userId),
          where('date', '>=', cutoffTimestamp),
          orderBy('date', 'desc')
        );
      } else {
        q = query(
          expensesRef, 
          where('userId', '==', userId),
          orderBy('date', 'desc')
        );
      }
      
      const querySnapshot = await getDocs(q);
      const expenses = [];
      
      querySnapshot.forEach((doc) => {
        expenses.push({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate?.() || new Date(doc.data().date)
        });
      });
      
      console.log(`Fetched ${expenses.length} expenses from Firestore`);
      
      // Cache the fetched data
      localStorage.setItem('cachedExpenses', JSON.stringify(expenses));
      localStorage.setItem('lastExpensesSync', new Date().toISOString());
      
      return expenses;
    } catch (error) {
      console.error('Error fetching expenses:', error);
      
      // Try to use cached data as fallback
      const cachedData = localStorage.getItem('cachedExpenses');
      if (cachedData) {
        return JSON.parse(cachedData);
      }
      
      throw error;
    }
  },
  
  // Add a new expense
  async addExpense(userId, expenseData) {
    try {
      const expensesRef = collection(db, 'expenses');
      
      // Ensure receipt data is properly included
      const dataToSave = {
        ...expenseData,
        userId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        // Explicitly include receipt fields if they exist
        ...(expenseData.receiptUrl && {
          receiptUrl: expenseData.receiptUrl,
          receiptId: expenseData.receiptId,
          receiptStoragePath: expenseData.receiptStoragePath,
          receiptName: expenseData.receiptName,
          receiptSize: expenseData.receiptSize,
          originalReceiptSize: expenseData.originalReceiptSize,
          compressionRatio: expenseData.compressionRatio,
          isPdf: expenseData.isPdf,
          hasReceipt: true
        })
      };
      
      console.log('Saving expense data to Firestore:', dataToSave);
      
      const docRef = await addDoc(expensesRef, dataToSave);
      return { id: docRef.id, ...dataToSave };
    } catch (error) {
      console.error('Error adding expense:', error);
      throw error;
    }
  },
  
  // Update an expense
  async updateExpense(expenseId, expenseData) {
    try {
      const expenseRef = doc(db, 'expenses', expenseId);
      
      // Ensure receipt data is properly included
      const dataToUpdate = {
        ...expenseData,
        updatedAt: Timestamp.now(),
        // Explicitly include receipt fields if they exist
        ...(expenseData.receiptUrl && {
          receiptUrl: expenseData.receiptUrl,
          receiptId: expenseData.receiptId,
          receiptStoragePath: expenseData.receiptStoragePath,
          receiptName: expenseData.receiptName,
          receiptSize: expenseData.receiptSize,
          originalReceiptSize: expenseData.originalReceiptSize,
          compressionRatio: expenseData.compressionRatio,
          isPdf: expenseData.isPdf,
          hasReceipt: true
        })
      };
      
      console.log('Updating expense data in Firestore:', dataToUpdate);
      
      await updateDoc(expenseRef, dataToUpdate);
      return { id: expenseId, ...dataToUpdate };
    } catch (error) {
      console.error('Error updating expense:', error);
      throw error;
    }
  },
  
  // Update delete method to mark as deleted instead of actually removing
  async deleteExpense(expenseId) {
    try {
      // Get the expense first
      const expenseRef = doc(db, 'expenses', expenseId);
      const expenseDoc = await getDoc(expenseRef);
      
      if (!expenseDoc.exists()) {
        console.error('Expense not found:', expenseId);
        return { success: false, error: 'Expense not found' };
      }
      
      const expenseData = expenseDoc.data();
      
      // Update with deleted status instead of deleting
      await updateDoc(expenseRef, {
        status: 'deleted',
        deletedAt: Timestamp.now()
      });
      
      // Update local cache if it exists
      const cachedData = localStorage.getItem('cachedExpenses');
      if (cachedData) {
        const expenses = JSON.parse(cachedData);
        const index = expenses.findIndex(e => e.id === expenseId);
        if (index !== -1) {
          expenses[index] = { 
            ...expenses[index], 
            status: 'deleted',
            deletedAt: new Date()
          };
          localStorage.setItem('cachedExpenses', JSON.stringify(expenses));
        }
      }
      
      return { success: true, id: expenseId };
    } catch (error) {
      console.error('Error marking expense as deleted:', error);
      throw error;
    }
  },
  
  // Get a single expense by ID
  async getExpense(expenseId) {
    try {
      const expenseDoc = await getDoc(doc(db, 'expenses', expenseId));
      if (!expenseDoc.exists()) {
        throw new Error('Expense not found');
      }
      
      const expenseData = expenseDoc.data();
      return {
        id: expenseDoc.id,
        ...expenseData,
        date: expenseData.date?.toDate?.() || new Date(expenseData.date)
      };
    } catch (error) {
      console.error('Error fetching expense:', error);
      throw error;
    }
  },
  
  // Get expenses for a specific date range
  async getExpensesByDateRange(userId, startDate, endDate) {
    try {
      const expensesRef = collection(db, 'expenses');
      const startTimestamp = Timestamp.fromDate(new Date(startDate));
      const endTimestamp = Timestamp.fromDate(new Date(endDate));
      
      const q = query(
        expensesRef,
        where('userId', '==', userId),
        where('date', '>=', startTimestamp),
        where('date', '<=', endTimestamp),
        orderBy('date', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const expenses = [];
      
      querySnapshot.forEach((doc) => {
        expenses.push({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate?.() || new Date(doc.data().date)
        });
      });
      return expenses;
    } catch (error) {
      console.error('Error fetching expenses by date range:', error);
      throw error;
    }
  },
  
  // Get expenses grouped by category for reporting
  async getExpensesByCategory(userId, startDate, endDate) {
    try {
      const expenses = await this.getExpensesByDateRange(userId, startDate, endDate);
      const categories = {};
      
      expenses.forEach(expense => {
        const categoryName = expense.category || 'other';
        if (!categories[categoryName]) {
          categories[categoryName] = {
            total: 0,
            expenses: []
          };
        }
        
        categories[categoryName].total += expense.totalAmount || 0;
        categories[categoryName].expenses.push(expense);
      });
      
      return Object.keys(categories).map(category => ({
        category,
        total: categories[category].total,
        count: categories[category].expenses.length
      }));
    } catch (error) {
      console.error('Error getting expenses by category:', error);
      throw error;
    }
  },
  
  // Get summary data for fiscal or calendar year
  async getYearSummary(userId, year, isFiscalYear = false) {
    try {
      // Set date range
      let startDate, endDate;
      
      if (isFiscalYear) {
        // UK fiscal year runs from April 6 to April 5
        startDate = new Date(year, 3, 6); // April 6
        endDate = new Date(year + 1, 3, 5); // April 5 of next year
      } else {
        // Calendar year
        startDate = new Date(year, 0, 1); // January 1
        endDate = new Date(year, 11, 31); // December 31
      }
      
      const expenses = await this.getExpensesByDateRange(userId, startDate, endDate);
      
      // Total expenses
      const total = expenses.reduce((sum, expense) => sum + (expense.totalAmount || 0), 0);
      
      // Group by month
      const monthlyData = Array(12).fill(0).map((_, i) => ({
        month: i,
        total: 0,
        count: 0
      }));
      
      expenses.forEach(expense => {
        const date = new Date(expense.date);
        const month = date.getMonth();
        
        monthlyData[month].total += expense.totalAmount || 0;
        monthlyData[month].count += 1;
      });
      
      return {
        total,
        count: expenses.length,
        monthlyData,
        startDate,
        endDate
      };
    } catch (error) {
      console.error('Error getting year summary:', error);
      throw error;
    }
  },
  
  // Get expenses for a specific tax year
  async getExpensesForTaxYear(userId, taxYearStr) {
    if (!userId) throw new Error('User ID is required');
    
    // Parse tax year (e.g., "2023-2024")
    const yearMatch = taxYearStr.match(/^(\d{4})-\d{4}$/);
    if (!yearMatch) throw new Error('Invalid tax year format');
    
    const startYear = parseInt(yearMatch[1], 10);
    
    // Tax year runs from April 6 to April 5
    const startDate = new Date(startYear, 3, 6); // April 6
    const endDate = new Date(startYear + 1, 3, 5); // April 5
    
    try {
      // Query expenses within the date range
      const expensesRef = collection(db, 'expenses');
      const q = query(
        expensesRef,
        where('userId', '==', userId),
        where('date', '>=', Timestamp.fromDate(startDate)),
        where('date', '<=', Timestamp.fromDate(endDate))
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting expenses for tax year:', error);
      return [];
    }
  },

  // Calculate tax-deductible mileage
  calculateMileageDeduction(mileage, ratePerMile = 0.45) {
    const miles = parseFloat(mileage);
    if (isNaN(miles)) return 0;
    
    // UK HMRC mileage allowance is 45p per mile for the first 10,000 miles
    return miles * ratePerMile;
  },
  
  // Generate export data in a specific format (e.g., for CSV export)
  generateExportData(expenses) {
    return expenses.map(expense => ({
      date: expense.date instanceof Date 
        ? expense.date.toLocaleDateString() 
        : new Date(expense.date).toLocaleDateString(),
      category: expense.category,
      description: expense.description,
      amount: expense.totalAmount,
      isMileage: expense.category === 'travel' && expense.mileage > 0,
      mileage: expense.mileage || 0,
      mileageRate: expense.mileageRate || 0.45
    }));
  },
  
  // Add sync method to synchronize cached expenses with Firebase
  async syncCachedExpenses(userId) {
    const cachedData = localStorage.getItem('cachedExpenses');
    if (!cachedData) return { synced: 0 };
    
    const cachedExpenses = JSON.parse(cachedData);
    const unsyncedExpenses = cachedExpenses.filter(exp => exp._needsSync);
    
    if (unsyncedExpenses.length === 0) return { synced: 0 };
    
    let syncedCount = 0;
    for (const expense of unsyncedExpenses) {
      try {
        // Remove the sync flag
        const { _needsSync, ...expenseData } = expense;
        
        if (!expense.id) {
          // New expense, needs to be added
          await this.addExpense(userId, expenseData);
        } else {
          // Existing expense, needs to be updated
          await this.updateExpense(expense.id, expenseData);
        }
        syncedCount++;
        
        // Update the cached version to remove sync flag
        const index = cachedExpenses.findIndex(e => 
          e.id === expense.id || 
          (e.description === expense.description && e.date.toString() === expense.date.toString())
        );
        if (index !== -1) {
          cachedExpenses[index] = { ...expense };
          delete cachedExpenses[index]._needsSync;
        }
      } catch (error) {
        console.error('Failed to sync expense:', expense, error);
      }
    }
    
    // Update the cache with synced items
    localStorage.setItem('cachedExpenses', JSON.stringify(cachedExpenses));
    
    return { synced: syncedCount };
  },

  getExpensesWithCache,
  invalidateExpensesCache,
  addExpenseWithCacheInvalidation,
  updateExpenseWithCacheInvalidation,

  // Get all expenses for a user
  async getExpenses(userId) {
    try {
      const expensesRef = collection(db, 'expenses');
      const q = query(
        expensesRef,
        where('userId', '==', userId),
        orderBy('date', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const expenses = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        expenses.push({
          id: doc.id,
          ...data,
          // Convert Firestore Timestamp to Date if needed
          date: data.date?.toDate ? data.date.toDate() : data.date,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
        });
      });
      
      return expenses;
    } catch (error) {
      console.error('Error fetching expenses:', error);
      throw error;
    }
  }
};
